import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  COMPANY_STATUS_IDS,
  COMPANY_STATUS_LABELS,
  COMPANY_TYPE_LABELS,
  companyHasActiveEmployees,
  createSortById,
  getCompanyDeleteText,
  isCompanyActivated,
  isCompanyDeactivated,
  scrollToTop,
  TableEmptyMessage,
} from "../../../components/Utils";
import {
  EditIconNavButton,
  ActivateIconButton,
  DeactivateIconButton,
  DeleteIconButton,
} from "../../../components/Buttons";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import SalesTableUtil from "../../../components/table/SalesTableUtil";
import TablePageReport from "../../../components/TablePageReport";

import { connect } from "react-redux";
import { frontloadConnect } from "react-frontload";
import {
  loadFilteredCompanies,
  activateCompany,
  deactivateCompany,
  deleteCompany,
  defaultFilter,
} from "../actions";
import { PermissionResource, Resources } from "../../Permissions";

const getFilterValues = (props, current) => {
  let values = { ...defaultFilter };
  if (current) {
    values = {
      ...values,
      rows: props.rows || defaultFilter.rows,
      ...current,
    };
  }
  return values;
};

const frontload = async (props) => {
  const filterValues = getFilterValues(props);
  await props.getCompanies(null, filterValues, true);
};
const mapStateToProps = (state) => {
  return {
    user: state.auth.userProfile,
    company: state.company.company,
    isLoading: state.company.loading,
    companies: state.company.companies,
    header: state.company.header,
  };
};
const mapDispatchToProps = (dispatch) => ({
  getCompanies: (...args) => dispatch(loadFilteredCompanies(...args)),
  activateCompany: (...args) => dispatch(activateCompany(...args)),
  deactivateCompany: (...args) => dispatch(deactivateCompany(...args)),
  deleteCompany: (...args) => dispatch(deleteCompany(...args)),
});

function CompanyTable({
  user,
  isLoading,
  companies,
  header,
  getCompanies,
  activateCompany,
  deactivateCompany,
  deleteCompany,
  rows = 10,
  className = "",
}) {
  const filterValuesRef = React.useRef(getFilterValues({ rows }));
  const filterTimerRef = React.useRef(null);
  const dtRef = React.useRef(null);
  const dialogRef = React.useRef(null);

  const [state, setState] = React.useState({
    first: 0,
    filteredCount: (companies || []).length,
  });
  React.useEffect(() => {
    scrollToTop();
  });

  const onEditCompany = (company, history) => {
    history.push(
      `/wizard/organisation/firma/${encodeURIComponent(company.id)}`,
    );
  };
  const onDeactivateCompany = (company) => {
    dialogRef.current.show(
      true,
      {
        title: "Servicepartner deaktivieren",
        text: `Wollen Sie den Servicepartner ${company.name} wirklich deaktivieren?`,
      },
      () => deactivateCompany(company, filterValuesRef.current, true),
    );
  };
  const onActivateCompany = (company) => {
    dialogRef.current.show(
      true,
      {
        title: "Servicepartner aktivieren",
        text: `Wollen Sie den Servicepartner ${company.name} wirklich aktivieren?`,
      },
      () => activateCompany(company, filterValuesRef.current, true),
    );
  };
  const onDeleteCompany = (company) => {
    dialogRef.current.show(
      true,
      {
        title: "Servicepartner löschen",
        text: getCompanyDeleteText(company),
      },
      () => deleteCompany(company, filterValuesRef.current, true),
    );
  };
  const actionTemplate = (rowData) => {
    const editId = `editCompanyBtn${rowData.id}`;
    const editBtn = (
      <PermissionResource resource={Resources.Organisation.CompanyWizard.Page}>
        <>
          <EditIconNavButton
            id={editId}
            name={editId}
            onClick={(history) => onEditCompany(rowData, history)}
          />
        </>
      </PermissionResource>
    );
    const actId = `actCompanyBtn${rowData.id}`;
    const activateBtn = isCompanyDeactivated(rowData) && (
      <ActivateIconButton
        id={actId}
        name={actId}
        onClick={() => onActivateCompany(rowData)}
      />
    );
    const deactId = `deactCompanyBtn${rowData.id}`;
    const deactivateBtn = isCompanyActivated(rowData) && (
      <DeactivateIconButton
        id={deactId}
        name={deactId}
        onClick={() => onDeactivateCompany(rowData)}
        disabled={companyHasActiveEmployees(rowData)}
      />
    );
    const deleteId = `deleteCompanyBtn${rowData.id}`;
    const deleteBtn = (
      <DeleteIconButton
        id={deleteId}
        name={deleteId}
        onClick={() => onDeleteCompany(rowData)}
        disabled={
          !isCompanyDeactivated(rowData) /* companyHasEmployees(rowData) */
        }
      />
    );
    return (
      <div>
        {editBtn}
        <PermissionResource
          resource={Resources.Organisation.CompanyWizard.Status}
          showOnlyIfEditable
        >
          <>
            {activateBtn}
            {deactivateBtn}
            {deleteBtn}
          </>
        </PermissionResource>
      </div>
    );
  };
  const onLazyLoad = (event) => {
    const filterValues = { ...filterValuesRef.current };
    filterValues.first = event.first;
    filterValues.rows = event.rows;
    const firstPage = event.first;
    getCompanies(
      () => setState((prev) => ({ ...prev, first: firstPage })),
      filterValues,
      true,
    );
  };
  const onValueChange = (nextValue) => {
    if (nextValue) {
      setState((prev) => ({
        ...prev,
        first: 0,
        filteredCount: nextValue.length,
      }));
    }
  };
  const onFilterChange = (field, value, noTimeout, noFilter) => {
    filterValuesRef.current[field] = value;
    if (noTimeout) {
      !noFilter && setState((prev) => ({ ...prev, first: 0 }));
      return !noFilter && getCompanies(null, filterValuesRef.current, true);
    }
    if (filterTimerRef.current) {
      clearTimeout(filterTimerRef.current);
    }
    filterTimerRef.current = setTimeout(() => {
      !noFilter && setState((prev) => ({ ...prev, first: 0 }));
      !noFilter && getCompanies(null, filterValuesRef.current, true);
    }, 1000);
  };
  const onSort = (event) => {
    filterValuesRef.current.sortFields = {};
    event.multiSortMeta.forEach((item) => {
      filterValuesRef.current.sortFields[item.field] = item.order;
    });
    getCompanies(null, { ...filterValuesRef.current }, true);
  };

  if (!companies) return null;
  const totalRecords = Number(
    header ? header["x-result-count"] : companies.length,
  );
  const { first, filteredCount } = state;
  const data = companies.map((company) => ({
    ...company,
    name: company.name.trim(),
    contractGiver: company.contractGiver,
    typeName: company.typeName || "KDL",
    address: company.address,
    status_id: company.status_id || COMPANY_STATUS_IDS.ACTIVATED,
    status:
      company.status ||
      COMPANY_STATUS_LABELS[
        `${company.status_id || COMPANY_STATUS_IDS.ACTIVATED}`
      ],
  }));

  // options map
  const typeOptions = Object.keys(COMPANY_TYPE_LABELS)
    .map((key) => {
      return {
        label: COMPANY_TYPE_LABELS[key],
        value: COMPANY_TYPE_LABELS[key],
      };
    })
    .filter((item) => item.label !== COMPANY_TYPE_LABELS["4"]);
  const isUserAdmin = false;
  let typeName = typeOptions.filter((item) =>
    isUserAdmin ? true : item.value !== COMPANY_TYPE_LABELS["1"],
  );
  if (data.length === 1) {
    typeName = typeName.filter((item) => item.value === data[0].typeName);
  }
  const status_id = [
    {
      label: COMPANY_STATUS_LABELS[COMPANY_STATUS_IDS.ACTIVATED],
      value: COMPANY_STATUS_IDS.ACTIVATED,
    },
    {
      label: COMPANY_STATUS_LABELS[COMPANY_STATUS_IDS.DEACTIVATED],
      value: COMPANY_STATUS_IDS.DEACTIVATED,
    },
  ];
  const optionMap = { typeName, status_id };

  // filters
  const useInputFilter = SalesTableUtil.createUseInputFilter(
    dtRef.current,
    400,
    onFilterChange,
  );
  const useMultiSelectFilter = SalesTableUtil.createUseMultiSelectFilter(
    dtRef.current,
    data,
    optionMap,
    onFilterChange,
  );
  const nameFilter = useInputFilter("name", { filterOnChange: true });
  const typeNameFilter = useMultiSelectFilter("typeName");
  const addressFilter = useInputFilter("address", { filterOnChange: true });
  const statusFilter = useMultiSelectFilter("status_id");

  const sortFields =
    data.length <= 1
      ? []
      : Object.keys(filterValuesRef.current.sortFields).map((field) => ({
          field,
          order: filterValuesRef.current.sortFields[field],
        }));
  return (
    <div className={`company-table clearfix ${className}`}>
      <DataTable
        value={data}
        rows={rows}
        ref={dtRef}
        onValueChange={onValueChange}
        first={first}
        lazy={true}
        totalRecords={totalRecords}
        onPage={(event) => onLazyLoad(event)}
        paginator
        paginatorLeft={
          <TablePageReport
            className="pad-lft"
            totalRecords={totalRecords}
            filteredRecords={filteredCount}
            first={first}
            rows={rows}
            emptyText="Keine Servicepartner"
            itemName="Servicepartner"
            itemNamePlural="Servicepartner"
          />
        }
        removableSort
        sortMode="multiple"
        multiSortMeta={sortFields}
        onSort={onSort}
        emptyMessage={
          isLoading ? null : (
            <TableEmptyMessage itemNamePlural="Servicepartner" />
          )
        }
      >
        <Column
          field="name"
          header="Firma"
          sortable={data.length > 1}
          filter
          {...nameFilter}
        />
        <Column
          field="typeName"
          header="Firmentyp"
          sortable={data.length > 1}
          filter
          {...typeNameFilter}
          style={{ width: "9em" }}
        />
        <Column field="address" header="Adresse" filter {...addressFilter} />
        <Column
          field="activeEmployees" // "totalEmployees"
          header="Aktive Mitarbeiter"
          style={{ textAlign: "center", width: "9em" }}
        />
        <Column
          field="status_id"
          header="Status"
          sortable={data.length > 1}
          filter
          {...statusFilter}
          body={(rowData) => rowData.status}
          style={{ textAlign: "center", width: "9em" }}
        />
        <Column
          body={actionTemplate}
          style={{ textAlign: "center", width: "10em" }}
        />
      </DataTable>
      <ConfirmationDialog ref={dialogRef} />
    </div>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  frontloadConnect(frontload, {
    onMount: true,
    onUpdate: false,
  })(CompanyTable),
);
